import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const QuickAdjustmentPanel = ({ selectedProducts, onAdjustmentSubmit, onClose }) => {
  const [adjustmentData, setAdjustmentData] = useState({
    type: 'adjustment', // adjustment, withdrawal
    reason: '',
    quantity: '',
    notes: '',
    responsibleEmployee: '',
    barcodeInput: ''
  });

  const [scannedProduct, setScannedProduct] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const adjustmentReasons = [
    { value: 'inventory-count', label: 'Recuento de Inventario' },
    { value: 'damaged-goods', label: 'Mercancía Dañada' },
    { value: 'expired-products', label: 'Productos Vencidos' },
    { value: 'theft-loss', label: 'Pérdida/Robo' },
    { value: 'supplier-return', label: 'Devolución a Proveedor' },
    { value: 'quality-control', label: 'Control de Calidad' },
    { value: 'system-correction', label: 'Corrección del Sistema' },
    { value: 'other', label: 'Otro' }
  ];

  const employees = [
    { id: 'emp001', name: 'Carlos Martínez' },
    { id: 'emp002', name: 'Ana García' },
    { id: 'emp003', name: 'Luis Rodríguez' },
    { id: 'emp004', name: 'María López' },
    { id: 'emp005', name: 'José Fernández' }
  ];

  const handleInputChange = (field, value) => {
    setAdjustmentData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBarcodeSubmit = (e) => {
    e.preventDefault();
    if (adjustmentData.barcodeInput.trim()) {
      // Mock barcode scanning - in real app this would lookup the product
      const mockProduct = {
        id: 'prod-' + adjustmentData.barcodeInput,
        name: 'Producto Escaneado',
        sku: adjustmentData.barcodeInput,
        currentStock: 25
      };
      setScannedProduct(mockProduct);
      setAdjustmentData(prev => ({ ...prev, barcodeInput: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const adjustmentPayload = {
        ...adjustmentData,
        products: scannedProduct ? [scannedProduct.id] : selectedProducts,
        timestamp: new Date().toISOString(),
        id: 'adj-' + Date.now()
      };

      await onAdjustmentSubmit(adjustmentPayload);
      
      // Reset form
      setAdjustmentData({
        type: 'adjustment',
        reason: '',
        quantity: '',
        notes: '',
        responsibleEmployee: '',
        barcodeInput: ''
      });
      setScannedProduct(null);
    } catch (error) {
      console.error('Error submitting adjustment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    return adjustmentData.reason && 
           adjustmentData.quantity && 
           adjustmentData.responsibleEmployee &&
           (selectedProducts.length > 0 || scannedProduct);
  };

  return (
    <div className="bg-surface rounded-lg border border-border">
      {/* Header */}
      <div className="px-6 py-4 border-b border-border bg-primary-50">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-heading-semibold text-text-primary">
            Ajuste Rápido de Stock
          </h3>
          {onClose && (
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={onClose}
            />
          )}
        </div>
      </div>

      <div className="p-6">
        {/* Barcode Scanner */}
        <div className="mb-6">
          <label className="block text-sm font-body-medium text-text-primary mb-2">
            Escanear Código de Barras
          </label>
          <form onSubmit={handleBarcodeSubmit} className="flex space-x-2">
            <Input
              type="text"
              placeholder="Escanear o introducir código..."
              value={adjustmentData.barcodeInput}
              onChange={(e) => handleInputChange('barcodeInput', e.target.value)}
              className="flex-1"
            />
            <Button
              type="submit"
              variant="secondary"
              iconName="Scan"
              disabled={!adjustmentData.barcodeInput.trim()}
            >
              Escanear
            </Button>
          </form>
        </div>

        {/* Scanned Product Display */}
        {scannedProduct && (
          <div className="mb-6 p-4 bg-accent-50 rounded-lg border border-accent-100">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-body-medium text-text-primary">
                  Producto Escaneado
                </h4>
                <p className="text-sm text-text-secondary">
                  {scannedProduct.name} - SKU: {scannedProduct.sku}
                </p>
                <p className="text-xs text-text-muted">
                  Stock actual: {scannedProduct.currentStock.toLocaleString('es-ES')}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                iconName="X"
                onClick={() => setScannedProduct(null)}
              />
            </div>
          </div>
        )}

        {/* Selected Products Info */}
        {selectedProducts.length > 0 && !scannedProduct && (
          <div className="mb-6 p-4 bg-secondary-50 rounded-lg">
            <h4 className="text-sm font-body-medium text-text-primary mb-2">
              Productos Seleccionados
            </h4>
            <p className="text-sm text-text-secondary">
              {selectedProducts.length} producto{selectedProducts.length !== 1 ? 's' : ''} seleccionado{selectedProducts.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}

        {/* Adjustment Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Adjustment Type */}
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              Tipo de Movimiento
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="type"
                  value="adjustment"
                  checked={adjustmentData.type === 'adjustment'}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  className="mr-2"
                />
                <span className="text-sm text-text-primary">Ajuste de Stock</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="type"
                  value="withdrawal"
                  checked={adjustmentData.type === 'withdrawal'}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  className="mr-2"
                />
                <span className="text-sm text-text-primary">Retirada sin Factura</span>
              </label>
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              Motivo *
            </label>
            <select
              value={adjustmentData.reason}
              onChange={(e) => handleInputChange('reason', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              required
            >
              <option value="">Seleccionar motivo...</option>
              {adjustmentReasons.map(reason => (
                <option key={reason.value} value={reason.value}>
                  {reason.label}
                </option>
              ))}
            </select>
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              Cantidad *
            </label>
            <div className="flex items-center space-x-2">
              <Input
                type="number"
                placeholder="Introducir cantidad..."
                value={adjustmentData.quantity}
                onChange={(e) => handleInputChange('quantity', e.target.value)}
                required
                min="1"
                className="flex-1"
              />
              <span className="text-sm text-text-secondary">unidades</span>
            </div>
          </div>

          {/* Responsible Employee */}
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              Empleado Responsable *
            </label>
            <select
              value={adjustmentData.responsibleEmployee}
              onChange={(e) => handleInputChange('responsibleEmployee', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              required
            >
              <option value="">Seleccionar empleado...</option>
              {employees.map(employee => (
                <option key={employee.id} value={employee.id}>
                  {employee.name}
                </option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              Notas Adicionales
            </label>
            <textarea
              value={adjustmentData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Información adicional sobre el ajuste..."
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setAdjustmentData({
                  type: 'adjustment',
                  reason: '',
                  quantity: '',
                  notes: '',
                  responsibleEmployee: '',
                  barcodeInput: ''
                });
                setScannedProduct(null);
              }}
            >
              Limpiar
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={isSubmitting}
              disabled={!isFormValid()}
              iconName="Save"
            >
              {isSubmitting ? 'Procesando...' : 'Registrar Ajuste'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickAdjustmentPanel;